import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Phone, Mail, Users, FileText, CheckSquare, Calendar, Check } from 'lucide-react';
import { crmActivitiesApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import CrmActivityForm from '../../components/crm/CrmActivityForm';
import type { CrmActivityType } from '../../types';

const TYPE_ICONS: Record<CrmActivityType, React.ReactNode> = {
  call:    <Phone className="w-4 h-4" />,
  email:   <Mail className="w-4 h-4" />,
  meeting: <Users className="w-4 h-4" />,
  note:    <FileText className="w-4 h-4" />,
  task:    <CheckSquare className="w-4 h-4" />,
};

// Domain category colors mapped onto semantic tokens (text + tinted tile).
const TYPE_COLOR: Record<CrmActivityType, string> = {
  call:    'var(--scpa-primary)',
  email:   'var(--scpa-accent)',
  meeting: 'var(--scpa-status-warning)',
  note:    'var(--scpa-text-muted)',
  task:    'var(--scpa-status-ok)',
};

export default function ActivityList() {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const queryClient = useQueryClient();
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['crm-activities', cid, type, status, page],
    queryFn: () => crmActivitiesApi.list({ type, status, page, limit: 50 }),
    enabled: !!cid,
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => crmActivitiesApi.complete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crm-activities'] }),
  });

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Activities"
        intro="Calls, emails, meetings, notes and tasks across your CRM. Log one or mark items complete."
        actions={
          <button onClick={() => setShowForm(true)} className="btn btn-primary flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Log Activity
          </button>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select className="select w-36" value={type} onChange={e => { setType(e.target.value); setPage(1); }}>
          <option value="">All types</option>
          <option value="call">Call</option>
          <option value="email">Email</option>
          <option value="meeting">Meeting</option>
          <option value="note">Note</option>
          <option value="task">Task</option>
        </select>
        <select className="select w-36" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="planned">Planned</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="space-y-2">
        {isLoading ? (
          [...Array(6)].map((_, i) => <div key={i} className="card h-16 animate-pulse" />)
        ) : !data?.activities.length ? (
          <div className="card">
            <EmptyState icon={<Calendar className="w-8 h-8" />}>
              No activities found. Use <b>Log Activity</b> to record one.
            </EmptyState>
          </div>
        ) : (
          data.activities.map(activity => {
            const tint = TYPE_COLOR[activity.type];
            return (
              <div key={activity.id} className={`card flex items-start gap-4 ${activity.status === 'completed' ? 'opacity-60' : ''}`}>
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ color: tint, backgroundColor: 'color-mix(in srgb, ' + tint + ' 14%, transparent)' }}
                >
                  {TYPE_ICONS[activity.type]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={`font-medium text-sm text-fg ${activity.status === 'completed' ? 'line-through' : ''}`}>{activity.subject}</p>
                      {activity.description && <p className="text-xs text-muted mt-0.5 line-clamp-1">{activity.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {activity.dueAt && (
                        <span className={`text-xs ${new Date(activity.dueAt) < new Date() && activity.status !== 'completed' ? 'text-err font-medium' : 'text-muted'}`}>
                          {new Date(activity.dueAt).toLocaleDateString()}
                        </span>
                      )}
                      {activity.status !== 'completed' && (
                        <button
                          onClick={() => completeMutation.mutate(activity.id)}
                          className="p-1.5 rounded-md text-muted hover:text-ok hover:bg-row-odd transition-colors"
                          title="Mark complete"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted">
                    {activity.contact && <span>Contact: {activity.contact.firstName} {activity.contact.lastName}</span>}
                    {activity.deal && <span>Deal: {activity.deal.title}</span>}
                    {activity.organization && <span>Org: {activity.organization.name}</span>}
                    {activity.owner && <span>· {activity.owner.name}</span>}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {data && data.total > 50 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted">{(page - 1) * 50 + 1}–{Math.min(page * 50, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <button className="btn btn-secondary" disabled={page * 50 >= data.total} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      )}

      {showForm && <CrmActivityForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); queryClient.invalidateQueries({ queryKey: ['crm-activities'] }); }} />}
    </div>
  );
}
